/**
 * 应收逾期预测分页查询
 */

/** 与 Excel 工作簿定义 / 权限 ViewAction 一致 */
export const RECEIVABLE_FORECAST_MODEL =
  'aries.platform.AriesPlatformReceivableForecast';

export const RECEIVABLE_FORECAST_MENU_PATH =
  '/aries_core_platform/uiMenuc7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2';

/** 导出 createExportTask variables.path */
export const RECEIVABLE_FORECAST_EXPORT_PATH =
  '/aries_core_platform/uiMenuc7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2/ACTION#aries.platform.AriesPlatformReceivableForecast#internalGotoListExportDialog';

/** 导入 createImportTask variables.path */
export const RECEIVABLE_FORECAST_IMPORT_PATH =
  '/aries_core_platform/uiMenuc7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2/ACTION#aries.platform.AriesPlatformReceivableForecast#internalGotoListImportDialog';

export interface ReceivableForecastToolbarAuth {
  canExport: boolean;
  canImport: boolean;
  canSendMail: boolean;
}

async function canAccessForecastViewAction(actionName: string): Promise<boolean> {
  try {
    const response = await fetch(`/pamirs/base`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        module: 'base',
      },
      body: JSON.stringify({
        operationName: null,
        variables: {
          needCompileView: false,
          needCompileMask: false,
          model: RECEIVABLE_FORECAST_MODEL,
          name: actionName,
          path: RECEIVABLE_FORECAST_MENU_PATH,
        },
        query: `query ($model: String, $name: String, $needCompileView: Boolean = false, $needCompileMask: Boolean = false) {
  viewActionQuery {
    load(
      request: {model: $model, name: $name, needCompileView: $needCompileView, needCompileMask: $needCompileMask}
    ) {
      name
    }
  }
}`,
      }),
    });
    if (!response.ok) {
      return false;
    }
    const result = await response.json();
    if (result?.errors?.length) {
      return false;
    }
    return !!result?.data?.viewActionQuery?.load?.name;
  } catch {
    return false;
  }
}

/**
 * 探测导出/导入 ViewAction 权限；无权限时隐藏按钮且不弹全局错误。
 */
export async function queryReceivableForecastToolbarAuth(): Promise<ReceivableForecastToolbarAuth> {
  const empty: ReceivableForecastToolbarAuth = {
    canExport: false,
    canImport: false,
    canSendMail: false,
  };
  try {
    const [canExport, canImport, canSendMail] = await Promise.all([
      canAccessForecastViewAction('internalGotoListExportDialog'),
      canAccessForecastViewAction('internalGotoListImportDialog'),
      canAccessForecastViewAction('sendForecastEmail'),
    ]);
    return { canExport, canImport, canSendMail };
  } catch (error) {
    console.warn('查询应收逾期预测工具栏权限失败', error);
    return empty;
  }
}

export interface ReceivableForecastItem {
  id: string;
  lDate?: string;
  isProfessional?: string;
  financeArea?: string;
  companyName?: string;
  channel?: string;
  controller?: string;
  financeOwner?: string;
  salesman?: string;
  salesmanNo?: string;
  customerCode?: string;
  customerName?: string;
  receivableBalance?: string;
  creditWithin?: string;
  overdueAmt?: string;
  monthEndDueAmt?: string;
  expectOverdueAmount?: string;
  expectRepayAmount?: string;
  overdueReason?: string;
  actualReceiptAmt?: string;
  actualMonthEndOverdueAmt?: string;
  repaymentRate?: string;
  /** 数据同步时间（forecast.dws_update_time） */
  dwsUpdateTime?: string;
}

export interface ReceivableForecastQueryParams {
  currentPage: number;
  size: number;
  lDate?: string;
  salesOfficeRegionCode?: string;
  saleOfficeCode?: string;
  sapCustomerCode?: string;
  financeCode?: string;
  salesmanUserCode?: string;
  saleChannelName?: string;
  isProfessional?: boolean;
}

interface ReceivableForecastRawItem {
  id?: number | string;
  lDate?: string;
  isProfessional?: number | string | boolean;
  salesOfficeRegionName?: string;
  saleOfficeName?: string;
  dissaleChannelName?: string;
  saleChannelName?: string;
  controlUserName?: string;
  financeName?: string;
  salesmanUserName?: string;
  salesmanUserCode?: string;
  custCode?: string;
  sapCustomerCode?: string;
  custName?: string;
  remainingReceivableAmt?: string;
  xyqxzCny?: string;
  overdueAmt?: string;
  monthEndDueAmt?: string;
  expectOverdueAmount?: number | string | null;
  expectRepayAmount?: string | null;
  riskDesc?: string;
  actualReceiptAmt?: string | null;
  actualMonthEndOverdueAmt?: string | null;
  repaymentRate?: string | null;
  dwsUpdateTime?: string | null;
}

interface ReceivableForecastQueryResponse {
  data?: {
    ariesPlatformReceivableForecastQuery?: {
      queryPage?: {
        content?: ReceivableForecastRawItem[];
        totalElements?: number | string;
        totalPages?: number | string;
        size?: number | string;
        currentPage?: number | string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

interface ReceivableForecastLDateOptionsResponse {
  data?: {
    ariesPlatformReceivableForecastQuery?: {
      queryLDateOptions?: string[] | null;
    };
  };
  errors?: Array<{ message?: string }>;
}

function emptyToBlank(value?: string) {
  return (value ?? '').trim();
}

function formatYesNo(value?: number | string | boolean) {
  if (value === 1 || value === '1' || value === true) return '是';
  if (value === 0 || value === '0' || value === false) return '否';
  return '-';
}

function formatAmount(value?: number | string | null) {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

function buildQueryData(params: ReceivableForecastQueryParams) {
  const data: Record<string, string | boolean> = {
    lDate: emptyToBlank(params.lDate),
    salesOfficeRegionCode: emptyToBlank(params.salesOfficeRegionCode),
    saleOfficeCode: emptyToBlank(params.saleOfficeCode),
    sapCustomerCode: emptyToBlank(params.sapCustomerCode),
    financeCode: emptyToBlank(params.financeCode),
    salesmanUserCode: emptyToBlank(params.salesmanUserCode),
    saleChannelName: emptyToBlank(params.saleChannelName),
  };
  if (typeof params.isProfessional === 'boolean') {
    data.isProfessional = params.isProfessional;
  }
  return data;
}

/** 导入模板预填用的列表筛选（与 queryPage queryData 一致） */
export function buildForecastImportQueryData(
  params: Omit<ReceivableForecastQueryParams, 'currentPage' | 'size'>,
): Record<string, unknown> {
  return buildQueryData({
    currentPage: 1,
    size: 1,
    ...params,
  });
}

function mapItem(raw: ReceivableForecastRawItem, fallbackLDate?: string): ReceivableForecastItem {
  return {
    id: String(raw.id ?? ''),
    lDate: raw.lDate || fallbackLDate || '',
    isProfessional: formatYesNo(raw.isProfessional),
    financeArea: raw.salesOfficeRegionName,
    companyName: raw.saleOfficeName,
    channel: raw.saleChannelName || raw.dissaleChannelName,
    controller: raw.controlUserName,
    financeOwner: raw.financeName,
    salesman: raw.salesmanUserName,
    salesmanNo: raw.salesmanUserCode,
    customerCode: raw.sapCustomerCode || raw.custCode,
    customerName: raw.custName,
    receivableBalance: formatAmount(raw.remainingReceivableAmt),
    creditWithin: formatAmount(raw.xyqxzCny),
    overdueAmt: formatAmount(raw.overdueAmt),
    monthEndDueAmt: formatAmount(raw.monthEndDueAmt),
    expectOverdueAmount: formatAmount(raw.expectOverdueAmount),
    expectRepayAmount: formatAmount(raw.expectRepayAmount),
    overdueReason: raw.riskDesc || '-',
    actualReceiptAmt: formatAmount(raw.actualReceiptAmt),
    actualMonthEndOverdueAmt: formatAmount(raw.actualMonthEndOverdueAmt),
    repaymentRate: raw.repaymentRate ? String(raw.repaymentRate) : '-',
    dwsUpdateTime: emptyToBlank(raw.dwsUpdateTime ?? undefined) || '-',
  };
}

function buildGraphql(currentPage: number, size: number, queryData: string) {
  return `{
  ariesPlatformReceivableForecastQuery {
    queryPage(
      page: {currentPage: ${currentPage}, size: ${size}}
      queryWrapper: {queryData: ${JSON.stringify(queryData)}}
    ) {
      content {
        id
        lDate
        isProfessional
        salesOfficeRegionName
        saleOfficeName
        dissaleChannelName
        saleChannelName
        controlUserName
        financeName
        salesmanUserName
        salesmanUserCode
        custCode
        sapCustomerCode
        custName
        remainingReceivableAmt
        xyqxzCny
        overdueAmt
        monthEndDueAmt
        expectOverdueAmount
        expectRepayAmount
        riskDesc
        actualReceiptAmt
        actualMonthEndOverdueAmt
        repaymentRate
        dwsUpdateTime
      }
      totalElements
      totalPages
      size
      currentPage
    }
  }
}`;
}

/**
 * 应收逾期预测分页查询
 */
export async function queryReceivableForecastPage(
  params: ReceivableForecastQueryParams,
): Promise<{ content: ReceivableForecastItem[]; totalElements: number }> {
  const queryData = JSON.stringify(buildQueryData(params));
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildGraphql(params.currentPage, params.size, queryData),
    }),
  });

  if (!response.ok) {
    throw new Error(`应收逾期预测接口请求失败: ${response.status}`);
  }

  const result: ReceivableForecastQueryResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '应收逾期预测 GraphQL 请求失败');
  }

  const pageData = result.data?.ariesPlatformReceivableForecastQuery?.queryPage;
  return {
    content: (pageData?.content ?? []).map((item) => mapItem(item, params.lDate)),
    totalElements: Number(pageData?.totalElements ?? 0),
  };
}

/**
 * 查询 risk 表中日期为 21 号的去重 l_date（倒序）
 */
export async function queryForecastLDateOptions(): Promise<string[]> {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: `{
  ariesPlatformReceivableForecastQuery {
    queryLDateOptions
  }
}`,
    }),
  });

  if (!response.ok) {
    throw new Error(`查询日期选项失败: ${response.status}`);
  }

  const result: ReceivableForecastLDateOptionsResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询日期选项 GraphQL 请求失败');
  }

  const list = result.data?.ariesPlatformReceivableForecastQuery?.queryLDateOptions;
  return Array.isArray(list) ? list.filter((d) => !!d && String(d).trim()) : [];
}

/**
 * 在 21 号日期列表中选离今天最近的一个（优先不大于今天）
 */
export function pickClosestLDate(dates: string[]): string | undefined {
  if (!dates?.length) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let bestPast: string | undefined;
  let bestFuture: string | undefined;
  let bestPastDiff = Number.POSITIVE_INFINITY;
  let bestFutureDiff = Number.POSITIVE_INFINITY;

  for (const raw of dates) {
    const text = String(raw || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) continue;
    const d = new Date(`${text}T00:00:00`);
    if (Number.isNaN(d.getTime())) continue;
    const diff = Math.abs(d.getTime() - today.getTime());
    if (d.getTime() <= today.getTime()) {
      if (diff < bestPastDiff) {
        bestPastDiff = diff;
        bestPast = text;
      }
    } else if (diff < bestFutureDiff) {
      bestFutureDiff = diff;
      bestFuture = text;
    }
  }
  return bestPast || bestFuture || dates[0];
}

function escapeGraphqlString(value: string) {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
}

export interface SaveExpectOverdueAmountParams {
  lDate: string;
  sapCustomerCode: string;
  expectOverdueAmount: number;
}

interface SaveExpectOverdueAmountResponse {
  data?: {
    ariesPlatformReceivableForecastMutation?: {
      saveExpectOverdueAmount?: {
        lDate?: string;
        sapCustomerCode?: string;
        expectOverdueAmount?: number | string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

/**
 * 保存预计逾期金额
 */
export async function saveExpectOverdueAmount(params: SaveExpectOverdueAmountParams) {
  const query = `mutation {
  ariesPlatformReceivableForecastMutation {
    saveExpectOverdueAmount(
      data: {
        lDate: "${escapeGraphqlString(params.lDate)}"
        sapCustomerCode: "${escapeGraphqlString(params.sapCustomerCode)}"
        expectOverdueAmount: "${escapeGraphqlString(String(params.expectOverdueAmount))}"
      }
    ) {
      lDate
      sapCustomerCode
      expectOverdueAmount
    }
  }
}`;

  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query,
    }),
  });

  if (!response.ok) {
    throw new Error(`保存预计逾期金额请求失败: ${response.status}`);
  }

  const result: SaveExpectOverdueAmountResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '保存预计逾期金额失败');
  }

  const saved = result.data?.ariesPlatformReceivableForecastMutation?.saveExpectOverdueAmount;
  if (!saved) {
    throw new Error('保存预计逾期金额失败：无返回数据');
  }
  return saved;
}

/** 预计逾期金额修改日志 */
export interface ExpectAmountRecordLogItem {
  id?: string;
  lDate?: string;
  sapCustomerCode?: string;
  originExpectOverdueAmount?: number | string | null;
  expectOverdueAmount?: number | string | null;
  createDate?: string;
  createName?: string;
  createWorkCode?: string;
}

export interface QueryExpectAmountRecordLogParams {
  lDate: string;
  sapCustomerCode: string;
}

interface QueryExpectAmountRecordLogResponse {
  data?: {
    ariesPlatformReceivableForecastQuery?: {
      queryExpectAmountRecordLog?: ExpectAmountRecordLogItem[];
    };
  };
  errors?: Array<{ message?: string }>;
}

/**
 * 查询预计逾期金额修改日志（倒序）
 */
export async function queryExpectAmountRecordLog(
  params: QueryExpectAmountRecordLogParams,
): Promise<ExpectAmountRecordLogItem[]> {
  const query = `{
  ariesPlatformReceivableForecastQuery {
    queryExpectAmountRecordLog(
      data: {
        lDate: "${escapeGraphqlString(params.lDate)}"
        sapCustomerCode: "${escapeGraphqlString(params.sapCustomerCode)}"
      }
    ) {
      id
      lDate
      sapCustomerCode
      originExpectOverdueAmount
      expectOverdueAmount
      createDate
      createName
      createWorkCode
    }
  }
}`;

  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query,
    }),
  });

  if (!response.ok) {
    throw new Error(`查询修改日志请求失败: ${response.status}`);
  }

  const result: QueryExpectAmountRecordLogResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询修改日志失败');
  }

  return result.data?.ariesPlatformReceivableForecastQuery?.queryExpectAmountRecordLog ?? [];
}

/** 与后端 AriesPlatformReceivableForecastImportTemplateAction 约定的查询条件前缀 */
const IMPORT_QUERY_PREFIX = '__IMPORT_QUERY__';

function encodeImportQueryBase64(raw: string) {
  const utf8 = unescape(encodeURIComponent(raw));
  return `base64:${btoa(utf8)}`;
}

export interface FetchForecastImportTemplateOptions {
  /** 勾选行 id；有值时只填勾选数据 */
  idList?: string[];
  /** 未勾选时带上列表筛选（与 queryPage queryData 一致） */
  queryData?: Record<string, unknown>;
}

interface FetchForecastImportTemplateUrlResponse {
  data?: {
    ariesPlatformReceivableForecastQuery?: {
      fetchImportTemplateUrl?: {
        redirectUri?: string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

function normalizeIdList(ids?: string[]) {
  if (!ids?.length) return [];
  return [...new Set(ids.map((id) => String(id ?? '').trim()).filter(Boolean))];
}

/**
 * 下载应收逾期预测导入模板（走预测独立 Action，不经过 ExcelWorkbookDefinition 报表扩展）。
 * 有勾选填勾选行，无勾选按查询条件填（与应收报表一致）。
 */
export async function fetchForecastImportTemplateUrl(
  workbookDefinitionId: string,
  options?: FetchForecastImportTemplateOptions,
): Promise<string> {
  const idList = normalizeIdList(options?.idList);
  const queryPayload = idList.length > 0 ? { idList } : (options?.queryData ?? {});
  const definitionContext = escapeGraphqlString(
    IMPORT_QUERY_PREFIX + encodeImportQueryBase64(JSON.stringify(queryPayload)),
  );
  const query = `{
  ariesPlatformReceivableForecastQuery {
    fetchImportTemplateUrl(data: {id: ${workbookDefinitionId}, definitionContext: "${definitionContext}"}) {
      redirectUri
    }
  }
}`;

  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query,
    }),
  });

  if (!response.ok) {
    throw new Error(`获取导入模板失败: ${response.status}`);
  }

  const result: FetchForecastImportTemplateUrlResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '获取导入模板失败');
  }

  const uri = result.data?.ariesPlatformReceivableForecastQuery?.fetchImportTemplateUrl?.redirectUri;
  if (!uri) {
    throw new Error('获取导入模板失败：无下载地址');
  }
  return uri;
}

export interface ForecastLastMailReceiver {
  emailType?: string;
  receiver?: string;
}

interface ForecastLastMailReceiverResponse {
  data?: {
    ariesPlatformReceivableForecastQuery?: {
      queryLastMailReceiver?: ForecastLastMailReceiver | null;
    };
  };
  errors?: Array<{ message?: string }>;
}

/** 查询最近一次预测邮件收件人（按邮件类型） */
export async function queryForecastLastMailReceiver(
  emailType = 'BEFORE_CLOSE',
): Promise<ForecastLastMailReceiver | null> {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: `{
  ariesPlatformReceivableForecastQuery {
    queryLastMailReceiver(data: {emailType: "${escapeGraphqlString(emailType)}"}) {
      emailType
      receiver
    }
  }
}`,
    }),
  });

  if (!response.ok) {
    throw new Error(`查询最近收件人失败: ${response.status}`);
  }

  const result: ForecastLastMailReceiverResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询最近收件人失败');
  }

  return result.data?.ariesPlatformReceivableForecastQuery?.queryLastMailReceiver ?? null;
}

export interface SendForecastEmailParams {
  lDate: string;
  emailType: string;
  receiver: string;
}

interface SendForecastEmailResponse {
  data?: {
    ariesPlatformReceivableForecastMutation?: {
      sendEmail?: {
        lDate?: string;
        emailType?: string;
        receiver?: string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

/** 发送应收逾期预测邮件 */
export async function sendForecastEmail(params: SendForecastEmailParams) {
  const query = `mutation {
  ariesPlatformReceivableForecastMutation {
    sendEmail(
      data: {
        lDate: "${escapeGraphqlString(params.lDate)}"
        emailType: "${escapeGraphqlString(params.emailType)}"
        receiver: "${escapeGraphqlString(params.receiver)}"
      }
    ) {
      lDate
      emailType
      receiver
    }
  }
}`;

  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query,
    }),
  });

  if (!response.ok) {
    throw new Error(`发送邮件失败: ${response.status}`);
  }

  const result: SendForecastEmailResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '发送邮件失败');
  }

  const sent = result.data?.ariesPlatformReceivableForecastMutation?.sendEmail;
  if (!sent) {
    throw new Error('发送邮件失败：无返回数据');
  }
  return sent;
}
